
import { useEffect } from "react";
import { useLanguage } from "./LanguageContext";
import { useLocation } from "react-router-dom";

type SchemaType = "Organization" | "WebSite" | "Service" | "Article" | "Offer" | "Review";

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonicalPath?: string;  // Relative path without domain
  noIndex?: boolean;       // Optional for pages that shouldn't be indexed
  schemaType?: SchemaType;
  schemaData?: Record<string, any>;
  alternateLanguages?: boolean; // Whether to include hreflang tags (default: true)
}

export const SEOHead: React.FC<SEOHeadProps> = ({ 
  title, 
  description, 
  canonicalPath,
  noIndex = false,
  schemaType,
  schemaData,
  alternateLanguages = true
}) => {
  const { language } = useLanguage();
  const location = useLocation();
  
  useEffect(() => {
    // Update document language
    document.documentElement.lang = language;
    
    // Set title
    if (title) {
      document.title = title;
    }
    
    // Set description
    if (description) {
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', description);
      } else {
        const newMetaDescription = document.createElement('meta');
        newMetaDescription.name = 'description';
        newMetaDescription.content = description;
        document.head.appendChild(newMetaDescription);
      }
    }
    
    // Set canonical URL
    let canonicalUrl;
    
    if (canonicalPath) {
      // If a specific canonical path is provided
      canonicalUrl = `https://trainerfolio.com${canonicalPath}`;
    } else {
      // Based on current route
      const currentPathname = location.pathname;
      
      // Determine if this is a localized path
      let basePath = currentPathname;
      if (!currentPathname.startsWith(`/${language}`) && language !== 'en') {
        basePath = `/${language}${currentPathname === '/' ? '' : currentPathname}`;
      }
      
      canonicalUrl = `https://trainerfolio.com${basePath}`;
    }
    
    // Update or create canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', canonicalUrl);
    } else {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      canonicalLink.setAttribute('href', canonicalUrl);
      document.head.appendChild(canonicalLink);
    }
    
    // Add alternate language links for hreflang tags
    if (alternateLanguages) {
      const createOrUpdateHreflangTag = (lang: string, url: string) => {
        const selector = `link[rel="alternate"][hreflang="${lang}"]`;
        let hreflangLink = document.querySelector(selector);
        
        if (hreflangLink) {
          hreflangLink.setAttribute('href', url);
        } else {
          hreflangLink = document.createElement('link');
          hreflangLink.setAttribute('rel', 'alternate');
          hreflangLink.setAttribute('hreflang', lang);
          hreflangLink.setAttribute('href', url);
          document.head.appendChild(hreflangLink);
        }
      };
      
      // Generate alternate URLs for each language
      const baseDomain = "https://trainerfolio.com";
      const pathWithoutLang = location.pathname.replace(/^\/(?:pt|es|fr)(?=\/|$)/, '') || '/';
      
      // Add hreflang tags for each supported language
      createOrUpdateHreflangTag('en', `${baseDomain}${pathWithoutLang}`);
      createOrUpdateHreflangTag('pt', `${baseDomain}/pt${pathWithoutLang === '/' ? '' : pathWithoutLang}`);
      createOrUpdateHreflangTag('es', `${baseDomain}/es${pathWithoutLang === '/' ? '' : pathWithoutLang}`);
      createOrUpdateHreflangTag('fr', `${baseDomain}/fr${pathWithoutLang === '/' ? '' : pathWithoutLang}`);
      
      // Add x-default hreflang
      createOrUpdateHreflangTag('x-default', `${baseDomain}${pathWithoutLang}`);
    } else {
      // Remove any existing hreflang tags if alternateLanguages is false
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    }
    
    // Manage indexing (robots)
    // Block indexing on non-production domains (lovable.app, lovableproject.com, etc.)
    const isProductionDomain = window.location.hostname === 'trainerfolio.com' || window.location.hostname === 'www.trainerfolio.com';
    const shouldNoIndex = noIndex || !isProductionDomain;
    
    let robotsTag = document.querySelector('meta[name="robots"]');
    if (shouldNoIndex) {
      if (!robotsTag) {
        robotsTag = document.createElement('meta');
        robotsTag.setAttribute('name', 'robots');
        document.head.appendChild(robotsTag);
      }
      robotsTag.setAttribute('content', 'noindex, nofollow');
    } else {
      if (robotsTag) {
        robotsTag.setAttribute('content', 'index, follow');
      }
    }

    // Add or update structured data Schema.org
    if (schemaType && schemaData) {
      // Remove existing schema script if any
      const existingSchema = document.querySelector('script[data-schema="true"]');
      if (existingSchema) {
        document.head.removeChild(existingSchema);
      }

      // Create basic structured data
      const schemaObj = {
        "@context": "https://schema.org",
        "@type": schemaType,
        ...schemaData
      };

      // Add schema script
      const schemaScript = document.createElement('script');
      schemaScript.type = 'application/ld+json';
      schemaScript.setAttribute('data-schema', 'true');
      schemaScript.textContent = JSON.stringify(schemaObj);
      document.head.appendChild(schemaScript);
    }
    
    return () => {
      // Clean up schema script if exists when unmounting
      const existingSchema = document.querySelector('script[data-schema="true"]');
      if (existingSchema) {
        document.head.removeChild(existingSchema);
      }
    };
  }, [title, description, canonicalPath, language, location.pathname, noIndex, schemaType, schemaData, alternateLanguages]);

  return null;  // This component doesn't render anything visually
};
